import { KeyRound, MonitorSmartphone } from 'lucide-react'
import { useId, useState } from 'react'

import { Button } from '@krp/ui-kit'

import '../../../../shared/ui/aqsi.css'
import type { MineVm, RmkActions } from '../../model/types'
import '../rmk.css'

/**
 * Рабочее место закреплено за другой вкладкой (RP-CA5): здесь ничего не отправляется, пока кассир не введёт свой код. После кода
 * работа переходит в эту вкладку, а прежняя сама покажет такой же экран. Решает сервер; экран только просит код и пересказывает отказ.
 * Работа под своим именем кода не имеет — переносится кнопкой.
 */
export function OtherTabScreen({ mine, actions }: { mine: MineVm; actions: RmkActions }) {
  const id = useId()
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const needsCode = mine.cashierId !== null
  const move = async () => {
    if (busy || (needsCode && value.trim() === '')) return
    setBusy(true)
    setProblem(null)
    const credential = value
    setValue('')
    try {
      setProblem(await actions.moveHere(credential))
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="aqsi-screen rmk-gate" aria-labelledby={`${id}-title`}>
      <header className="aqsi-toolbar">
        <h2 id={`${id}-title`}>
          <MonitorSmartphone size={18} aria-hidden="true" /> Рабочее место открыто в другой вкладке
        </h2>
      </header>
      <p className="aqsi-caption">
        Касса {mine.registerName}
        {mine.cashierName !== null ? `, кассир ${mine.cashierName}` : ''}. Пока рабочее место там, из этой вкладки ничего не отправляется.
        {needsCode ? ' Введите код кассира, чтобы перенести рабочее место сюда: в той вкладке оно закроется.' : ' Перенесите рабочее место сюда: в той вкладке оно закроется.'}
      </p>
      <form
        className="aqsi-form rmk-code"
        aria-label="Перенос рабочего места в эту вкладку"
        onSubmit={(event) => {
          event.preventDefault()
          void move()
        }}
      >
        {needsCode && (
          <>
            <label className="krp-field__label" htmlFor={id}>
              Код кассира {mine.cashierName ?? ''}
            </label>
            <input
              id={id}
              className="rmk-code__input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
              data-lpignore="true"
              data-1p-ignore="true"
              value={value}
              disabled={busy}
              onChange={(event) => setValue(event.target.value)}
            />
          </>
        )}
        <Button
          type="submit"
          variant="primary"
          icon={<KeyRound size={16} aria-hidden="true" />}
          actionId="aqsi.rmk.move-here"
          busy={busy}
          disabled={busy || (needsCode && value.trim() === '')}
          disabledReason="Введите код кассира."
        >
          Перенести сюда
        </Button>
      </form>
      {problem !== null && (
        <p className="aqsi-card__text" role="alert">
          {problem}
        </p>
      )}
    </section>
  )
}
